import type { ErrorCode, ErrorDetail } from '@/types/api';

/**
 * Every failed request surfaces as one of these, whatever went wrong -
 * a 4xx envelope, a network failure or an unparseable body. UI code can
 * therefore handle errors in one shape instead of three.
 */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: ErrorDetail[];
  readonly requestId: string | undefined;

  constructor(params: {
    message: string;
    code: ErrorCode;
    status: number;
    details?: ErrorDetail[];
    requestId?: string;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.code = params.code;
    this.status = params.status;
    this.details = params.details ?? [];
    this.requestId = params.requestId;
  }

  /** True when re-authenticating could plausibly fix this. */
  get isAuthError(): boolean {
    return this.code === 'UNAUTHORIZED' || this.code === 'FORBIDDEN';
  }

  /** Field-level messages, keyed by field, for wiring into form state. */
  get fieldErrors(): Record<string, string> {
    return this.details.reduce<Record<string, string>>((acc, detail) => {
      /* Strip the `body.` / `query.` prefix the backend adds. */
      const field = detail.field.replace(/^(body|query|params)\./, '');
      acc[field] = detail.message;
      return acc;
    }, {});
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}
